import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    MdClose,
    MdOpenInNew,
    MdPersonOutline,
    MdOutlineLocationOn,
    MdOutlineHistory,
} from "react-icons/md";

import InputField from "components/fields/InputField";
import InputTagField from "components/fields/InputTagField";
import SearchSelector from "components/fields/SearchSelector";
import SaveStatus from "components/actions/SaveStatus";
import { getCustomerAcquisitionYears, getCustomerPage, updateCustomer } from "interfaces/customer";
import { getMyInfo, updateMyInfo } from "interfaces/user";
import CustomerComments from "../../editCustomer/components/Comments";
import { isValidEmail, isValidPhoneNumber } from "utils/verification";

const SectionHeader = (props: { icon: any; title: string }) => {
    const { icon: Icon, title } = props;
    return (
        <div className="flex items-center gap-2">
            <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-[#01499b]/10 text-[#01499b] dark:bg-white/10 dark:text-white">
                <Icon className="h-3.5 w-3.5" />
            </div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                {title}
            </p>
        </div>
    );
};

const GroupLabel = (props: { children: any; first?: boolean }) => {
    const { children, first = false } = props;
    return (
        <p
            className={`col-span-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 ${
                first
                    ? ""
                    : "mt-1 border-t border-gray-100 pt-3 dark:border-white/10"
            }`}
        >
            {children}
        </p>
    );
};

const EDITABLE_FIELDS = [
    "displayName",
    "company",
    "contact",
    "contactPoste",
    "website",
    "mail",
    "phone",
    "tags",
    "assignedTo",
    "statut",
    "raisonPerte",
    "anneeAcquisition",
    "typeMembre",
    "deliveryAddress",
    "deliveryZipCode",
    "deliveryCity",
    "francoPort",
    "billingAddress",
    "billingZipCode",
    "billingCity",
    "siren",
    "vatNumber",
] as const;

const CustomerQuickEditPanel = (props: {
    customerId: string;
    users: any[];
    onClose: () => void;
    onSaved: (id: string, patch: any) => void;
}) => {
    const { customerId, users, onClose, onSaved } = props;
    const navigate = useNavigate();
    const [customer, setCustomer] = useState<any>(null);
    const [saveStatus, setSaveStatus] = useState<string>("none");
    const [me, setMe] = useState<any>(null);
    const [isBillingAddress, setIsBillingAddress] = useState(false);
    const [acquisitionYears, setAcquisitionYears] = useState<string[]>([]);
    const isFirstLoad = useRef(true);

    useEffect(() => {
        isFirstLoad.current = true;
        setCustomer(null);
        setSaveStatus("none");
        setIsBillingAddress(false);

        (async () => {
            const res = await getCustomerPage(customerId);
            if (res) {
                if (res.billingAddress) setIsBillingAddress(true);
                setCustomer(res);
            }
        })();
        (async () => {
            const res = await getMyInfo();
            if (res?.data) setMe(res.data);
        })();
        (async () => {
            const res = await getCustomerAcquisitionYears();
            if (res) setAcquisitionYears(res);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId]);

    useEffect(() => {
        if (!customer) return;
        if (isFirstLoad.current) {
            isFirstLoad.current = false;
            return;
        }
        setSaveStatus("saving");
        const timer = setTimeout(async () => {
            const patch: any = {};
            EDITABLE_FIELDS.forEach((f) => {
                patch[f] = customer[f];
            });
            const res = await updateCustomer({ _id: customer._id, ...patch });
            if (res?.data) {
                setSaveStatus("saved");
                onSaved(customer._id, patch);
            } else {
                setSaveStatus("error");
            }
        }, 900);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        customer?.displayName,
        customer?.company,
        customer?.contact,
        customer?.contactPoste,
        customer?.website,
        customer?.mail,
        customer?.phone,
        customer?.tags,
        customer?.assignedTo,
        customer?.statut,
        customer?.raisonPerte,
        customer?.anneeAcquisition,
        customer?.typeMembre,
        customer?.deliveryAddress,
        customer?.deliveryZipCode,
        customer?.deliveryCity,
        customer?.francoPort,
        customer?.billingAddress,
        customer?.billingZipCode,
        customer?.billingCity,
        customer?.siren,
        customer?.vatNumber,
    ]);

    const handleChange = (e: any) => {
        setCustomer((prev: any) => ({ ...prev, [e.target.id]: e.target.value }));
    };

    const handleChangeBilling = (e: any) => {
        if (isBillingAddress) {
            handleChange(e);
            return;
        }
        setCustomer((prev: any) => ({
            ...prev,
            deliveryAddress: e.target.value,
            billingAddress: e.target.value,
        }));
    };

    const handleChangeDeliveryZip = (e: any) => {
        if (isBillingAddress) {
            handleChange(e);
            return;
        }
        setCustomer((prev: any) => ({
            ...prev,
            deliveryZipCode: e.target.value,
            billingZipCode: e.target.value,
        }));
    };

    const handleChangeDeliveryCity = (e: any) => {
        if (isBillingAddress) {
            handleChange(e);
            return;
        }
        setCustomer((prev: any) => ({
            ...prev,
            deliveryCity: e.target.value,
            billingCity: e.target.value,
        }));
    };

    const addNewTag = async (newTag: string) => {
        if (!me) return;
        await updateMyInfo({ tags: [...(me.tags || []), newTag] });
        setMe((prev: any) => ({ ...prev, tags: [...(prev.tags || []), newTag] }));
    };

    const addMemberType = async (newType: string) => {
        if (!me) return;
        const updated = [...(me.memberTypes || []), newType];
        await updateMyInfo({ memberTypes: updated });
        setMe((prev: any) => ({ ...prev, memberTypes: updated }));
    };

    return (
        <div className="fixed inset-0 z-50 flex justify-end">
            <div className="absolute inset-0 bg-black/30" onClick={onClose} />
            <div className="animate-slideInRight relative z-10 flex h-full w-full max-w-lg flex-col overflow-hidden bg-white shadow-2xl dark:bg-navy-800 dark:shadow-none">
                {/* Header */}
                <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-6 py-5 dark:border-white/10">
                    <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#01499b]/10 text-sm font-bold text-[#01499b] dark:bg-white/10 dark:text-white">
                            {customer?.company
                                ? customer.company.charAt(0).toUpperCase()
                                : '?'}
                        </div>
                        <div className="min-w-0">
                            <h3 className="truncate text-sm font-bold text-navy-700 dark:text-white">
                                {customer ? (customer.company || customer.displayName) : "Chargement..."}
                            </h3>
                            {customer && (
                                <button
                                    className="mt-0.5 flex items-center gap-1 text-xs text-toola-600 hover:underline dark:text-blue-400"
                                    onClick={() => navigate(`/customer/${customer._id}`)}
                                >
                                    <MdOpenInNew className="h-3 w-3" />
                                    Voir la fiche complète
                                </button>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="shrink-0 rounded-full p-1.5 text-gray-400 transition duration-200 hover:bg-gray-100 hover:text-gray-600 dark:text-gray-500 dark:hover:bg-white/10 dark:hover:text-gray-300"
                    >
                        <MdClose className="h-4 w-4" />
                    </button>
                </div>

                <div className="custom-scrollbar flex-1 overflow-y-auto px-6 py-5 space-y-6">
                    {/* Quick edit form */}
                    <div>
                        <div className="mb-3 flex items-center justify-between">
                            <SectionHeader
                                icon={MdPersonOutline}
                                title="Informations client"
                            />
                            <SaveStatus status={saveStatus} />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <GroupLabel first>Identité</GroupLabel>
                            <div className="col-span-2">
                                <InputField
                                    label="Raison sociale*"
                                    value={customer?.company || ""}
                                    id="company"
                                    type="text"
                                    onChange={handleChange}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-2">
                                <InputField
                                    label="Nom commercial"
                                    value={customer?.displayName || ""}
                                    id="displayName"
                                    type="text"
                                    onChange={handleChange}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>

                            <GroupLabel>Contact</GroupLabel>
                            <div>
                                <InputField
                                    label="Contact"
                                    value={customer?.contact || ""}
                                    id="contact"
                                    type="text"
                                    onChange={handleChange}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>
                            <div>
                                <InputField
                                    label="Poste du contact"
                                    value={customer?.contactPoste || ""}
                                    id="contactPoste"
                                    type="text"
                                    onChange={handleChange}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-2">
                                <InputField
                                    label="Site web"
                                    value={customer?.website || ""}
                                    id="website"
                                    type="text"
                                    onChange={handleChange}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>
                            <div>
                                <InputField
                                    label="Email"
                                    value={customer?.mail || ""}
                                    id="mail"
                                    type="email"
                                    error="Email invalide"
                                    isInvalid={!!customer?.mail && !isValidEmail(customer?.mail)}
                                    onChange={handleChange}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>
                            <div>
                                <InputField
                                    label="Téléphone"
                                    value={customer?.phone || ""}
                                    id="phone"
                                    type="text"
                                    error="Téléphone invalide"
                                    isInvalid={!!customer?.phone && !isValidPhoneNumber(customer?.phone)}
                                    onChange={handleChange}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>

                            <GroupLabel>Suivi commercial</GroupLabel>
                            <div className="col-span-2 z-10">
                                <SearchSelector
                                    label="Commercial assigné"
                                    displayKey="fullName"
                                    options={users || []}
                                    value={
                                        customer?.assignedTo?.length > 0
                                            ? customer.assignedTo[0]
                                            : null
                                    }
                                    onSelect={(u: any) =>
                                        setCustomer((prev: any) => ({
                                            ...prev,
                                            assignedTo: [u._id],
                                        }))
                                    }
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-2 z-10">
                                <SearchSelector
                                    label="Statut"
                                    displayKey="name"
                                    options={[
                                        { _id: 'actif', name: 'Client actif' },
                                        { _id: 'prospect', name: 'Prospect' },
                                        { _id: 'perdu', name: 'Perdu' },
                                        { _id: 'liquidation', name: 'Liquidation' },
                                        { _id: 'autre', name: 'Autre' },
                                    ]}
                                    value={customer?.statut || 'actif'}
                                    onSelect={(e: any) =>
                                        setCustomer((prev: any) => ({
                                            ...prev,
                                            statut: e._id,
                                        }))
                                    }
                                    inputSmall={true}
                                />
                            </div>
                            {customer?.statut === 'perdu' && (
                                <div className="col-span-2">
                                    <InputField
                                        label="Raison de la perte"
                                        value={customer?.raisonPerte || ""}
                                        id="raisonPerte"
                                        type="text"
                                        onChange={handleChange}
                                        isLoaded={customer}
                                        inputSmall={true}
                                    />
                                </div>
                            )}
                            <div className="z-10">
                                <SearchSelector
                                    label="Année d'acquisition"
                                    displayKey="name"
                                    options={acquisitionYears.map((y: string) => ({ _id: y, name: y }))}
                                    value={customer?.anneeAcquisition}
                                    onSelect={(e: any) =>
                                        setCustomer((prev: any) => ({
                                            ...prev,
                                            anneeAcquisition: e._id,
                                        }))
                                    }
                                    onCreateOption={(newYear: string) =>
                                        setAcquisitionYears((prev) => [...prev, newYear])
                                    }
                                    inputSmall={true}
                                />
                            </div>
                            <div className="z-10">
                                <SearchSelector
                                    label="Type de membre"
                                    displayKey="name"
                                    options={(me?.memberTypes || []).map(
                                        (t: string) => ({ _id: t, name: t }),
                                    )}
                                    value={customer?.typeMembre}
                                    onSelect={(e: any) =>
                                        setCustomer((prev: any) => ({
                                            ...prev,
                                            typeMembre: e._id,
                                        }))
                                    }
                                    onCreateOption={addMemberType}
                                    inputSmall={true}
                                />
                            </div>

                            <GroupLabel>Fiscal</GroupLabel>
                            <div>
                                <InputField
                                    label="Numéro de SIREN"
                                    value={customer?.siren || ""}
                                    id="siren"
                                    type="text"
                                    onChange={handleChange}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>
                            <div>
                                <InputField
                                    label="Numéro de TVA"
                                    value={customer?.vatNumber || ""}
                                    id="vatNumber"
                                    type="text"
                                    onChange={handleChange}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>

                            <GroupLabel>Tags</GroupLabel>
                            <div className="col-span-2">
                                <InputTagField
                                    label="Tags"
                                    placeholder=""
                                    id="tags"
                                    type="text"
                                    tags={customer?.tags}
                                    allTags={me ? (me.tags || []).map((name: string) => ({ name })) : []}
                                    onTagCreate={(tag: any) => addNewTag(tag)}
                                    onChange={(tags: any) =>
                                        setCustomer((prev: any) => ({ ...prev, tags }))
                                    }
                                />
                            </div>
                        </div>
                    </div>

                    {/* Addresses */}
                    <div>
                        <SectionHeader
                            icon={MdOutlineLocationOn}
                            title="Adresses"
                        />
                        <div className="mt-3 grid grid-cols-2 gap-4">
                            <GroupLabel first>Livraison</GroupLabel>
                            <div className="col-span-2">
                                <InputField
                                    label="Adresse de livraison*"
                                    value={customer?.deliveryAddress || ""}
                                    id="deliveryAddress"
                                    type="text"
                                    onChange={handleChangeBilling}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>
                            <div>
                                <InputField
                                    label="Code postal"
                                    value={customer?.deliveryZipCode || ""}
                                    id="deliveryZipCode"
                                    type="text"
                                    onChange={handleChangeDeliveryZip}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>
                            <div>
                                <InputField
                                    label="Ville"
                                    value={customer?.deliveryCity || ""}
                                    id="deliveryCity"
                                    type="text"
                                    onChange={handleChangeDeliveryCity}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-2">
                                <InputField
                                    label="Franco de port"
                                    value={customer?.francoPort || ""}
                                    id="francoPort"
                                    type="text"
                                    onChange={handleChange}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>

                            <GroupLabel>Facturation</GroupLabel>
                            <div className="col-span-2">
                                <InputField
                                    label="Adresse de facturation*"
                                    value={customer?.billingAddress || ""}
                                    id="billingAddress"
                                    type="text"
                                    onChange={handleChange}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>
                            <div>
                                <InputField
                                    label="Code postal"
                                    value={customer?.billingZipCode || ""}
                                    id="billingZipCode"
                                    type="text"
                                    onChange={handleChange}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>
                            <div>
                                <InputField
                                    label="Ville"
                                    value={customer?.billingCity || ""}
                                    id="billingCity"
                                    type="text"
                                    onChange={handleChange}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Activity */}
                    <div>
                        <SectionHeader
                            icon={MdOutlineHistory}
                            title="Activité récente"
                        />
                        <div className="mt-3">
                            {customer && customer.activities ? (
                                <CustomerComments id={customer._id} comments={customer.activities} />
                            ) : (
                                <p className="text-sm text-gray-400">Chargement...</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomerQuickEditPanel;
